import type { Dispatch } from "react";
import type { EditorAction } from "../../editor/reducers";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { assertTypeName } from "../../client/guards";
import { TextFormField } from "../../attributes/text/TextFormField";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { Language } from "../../rich-text/extension-language";
import { FormulaRef } from "../../rich-text/extension-formula-ref";
import { FootnoteRef } from "../../rich-text/extension-footnote-ref";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  useTextRegionEditorSuspenseQuery,
  useUpdateTextRegionMutation,
} from "../../client/generated";

type Props = {
  textAttributeId: string;
  dispatch: Dispatch<EditorAction>;
};

export function TextRegionEditor({ textAttributeId, dispatch }: Props) {
  const { data } = useTextRegionEditorSuspenseQuery({
    variables: {
      textAttributeId,
    },
  });

  const { textAttr } = data;
  assertTypeName(textAttr?.__typename === "TextAttribute");

  const [updateRegion] = useUpdateTextRegionMutation();
  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
        FormulaRef.configure({ fragment: textAttr.region.page }),
        FootnoteRef.configure({ fragment: textAttr.region.page }),
      ],
      content: textAttr.text,
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editor) return false;

    const result = await updateRegion({
      variables: {
        textAttributeId,
        text: editor.getHTML(),
      },
    });

    return (
      !result.errors && !result.data?.updateTextAttribute.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <TextFormField fragment={textAttr.region} editor={editor} />
    </RegionUpdateForm>
  );
}
